// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ResourceView, SurveyPart, SurveyType } from '../../../api/types';
import { partMassText } from '../../../bygning/measure';
import { isScanPart } from '../../../bygning/provenance';
import type { Badge } from '../../../bygning/review';
import { roomName } from '../../../kortlaegning/model';
import { formatNumber } from '../../../kortlaegning/vocab';
import { Pill } from '../../Pill';
import { CardImage } from './GalleryCard';
import styles from './Galleri.module.css';

export interface GalleryPartCardProps {
  part: SurveyPart;
  type: SurveyType;
  /** Its index in the grid (`data-index`, for the arrow keys); null for a part marked as an error (not in the walk). */
  index: number | null;
  selected: boolean;
  /** The grid's one tab stop (roving tabindex). */
  tabbable: boolean;
  /** The type's lens colour (a token name); null = the ghost swatch. */
  swatch: string | null;
  /** The part's own resource image (`sys:view`); null = the placeholder. */
  view: ResourceView | null;
  placeholder: { token: string; text: string };
  /** The type's review pill, as Tabel › Dele shows it. */
  review: Badge;
  /** Marked as an error: muted, not selectable, "Fejldetektion" (as Tabel › Dele's last rows). */
  dismissed?: boolean;
  onSelect?: () => void;
  /** Double-click on a card with an image: open the part's Foto evidence. */
  onOpen?: () => void;
}

/**
 * One part in Galleri › Dele: its own resource image (4:3), then the type's
 * swatch and name, the code and room, mængde and masse, and Tabel › Dele's
 * status pills. A button with `aria-pressed`: a click focuses the part.
 */
export function GalleryPartCard({
  part,
  type,
  index,
  selected,
  tabbable,
  swatch,
  view,
  placeholder,
  review,
  dismissed = false,
  onSelect,
  onOpen,
}: GalleryPartCardProps) {
  const room = roomName(part);
  const mass = dismissed ? null : partMassText(part);
  const body = (
    <>
      <CardImage view={view} placeholder={placeholder} />
      <span className={styles.body}>
        <span className={styles.head}>
          <i
            className={swatch ? styles.swatch : `${styles.swatch} ${styles.ghost}`}
            style={swatch ? { background: `var(${swatch})` } : undefined}
            aria-hidden="true"
          />
          <span className={styles.name} title={type.name}>
            {type.name}
          </span>
        </span>
        <span className={styles.partLine}>
          {part.starred && (
            <span className={styles.star} role="img" aria-label="Vigtig">
              ★{' '}
            </span>
          )}
          <span className={styles.code}>{part.code}</span>
          {room && ` · ${room}`}
        </span>
        <span className={styles.desc}>
          {formatNumber(part.quantity)} {type.unit}
          {mass && ` · ${mass}`}
        </span>
        <span className={styles.pills}>
          {dismissed ? (
            <Pill variant="outline">Fejldetektion</Pill>
          ) : (
            <>
              <Pill tone={review.tone}>{review.label}</Pill>
              {!isScanPart(part) && <Pill variant="outline">Manuel</Pill>}
              {part.orphaned && (
                <Pill tone="warn" title="Instansen findes ikke længere — gennemgå eller flyt delen">
                  forældet
                </Pill>
              )}
            </>
          )}
        </span>
      </span>
    </>
  );

  if (dismissed) {
    return (
      <div className={`${styles.card} ${styles.cardDismissed}`} role="group" aria-label={`${part.code} — markeret som fejldetektion`}>
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={styles.card}
      aria-pressed={selected}
      data-part-code={part.code}
      data-index={index ?? undefined}
      tabIndex={tabbable ? 0 : -1}
      onClick={onSelect}
      onDoubleClick={onOpen}
    >
      {body}
    </button>
  );
}
